import { useRef, useCallback, useContext } from 'react'
import { useIntl } from 'react-intl'
import CruiseWhenSelectContent from './CruiseWhenSelectContent'
import { GlobalSearchDispatchContext, GlobalSearchStateContext } from 'contexts/GlobalSearch/GlobalSearchContexts'
import { GlobalSearchStateActions } from 'contexts/GlobalSearch/GlobalSearchState'
import Group from 'components/utils/Group'
import ModalBase from 'LuxKit/Modal/ModalBase'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalFooter from 'LuxKit/Modal/ModalFooter'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import CruiseFilterFooter from '../Common/CruiseFilterFooter'
import CruiseFilterInput from 'components/Cruises/SearchPage/Filters/Inputs/Common/CruiseFilterInput'
import { useCruiseSearchInput } from '../CruiseSearchInputContext'
import { INTL_DMY_CASUAL_FORMAT } from 'constants/dateFormats'
import { intlDateFormatter } from 'lib/datetime/dateUtils'

import { trackClientEvent, trackEvent } from 'analytics/analytics'
import { mapCruiseLineFilterToSnowplowSearchEvent } from 'analytics/mapSnowplowSearchTracking'
import { searchEventWithContext } from 'analytics/snowplow/events'

interface Props {
  inputValue: string
  isChipMode?: boolean
  initialFilters?: App.CruiseInitialFilters
  onChange: (values: App.CruiseGlobalFilters) => void
}

function CruiseWhenSelectMobile({ inputValue, onChange, isChipMode, initialFilters }: Props) {
  const intl = useIntl()
  const dropdownTriggerRef = useRef<HTMLButtonElement>(null)
  const { isMobileOpen, closeMobile, toggleMobile } = useCruiseSearchInput()
  const searchDispatch = useContext(GlobalSearchDispatchContext)
  const globalSearchState = useContext(GlobalSearchStateContext)
  const {
    flexibleMonths,
    durationMax,
    durationMin,
    durationRange,
    checkinDate,
    checkoutDate,
  } = globalSearchState

  const onClear = useCallback(() => {
    trackClientEvent({
      subject: 'filter_cleared',
      action: 'cleared',
      category: 'cruise_when_filter',
      type: 'interaction',
    })
    searchDispatch({ type: GlobalSearchStateActions.SET_DURATION_RANGE, min: undefined, max: undefined })
    searchDispatch({ type: GlobalSearchStateActions.SET_FLEXIBLE_MONTH_RANGE, flexibleMonths: '' })
    searchDispatch({ type: GlobalSearchStateActions.SET_CHECKIN_DATE, date: undefined })
    searchDispatch({ type: GlobalSearchStateActions.SET_CHECKOUT_DATE, date: undefined })
    searchDispatch({ type: GlobalSearchStateActions.SET_FLEXIBLE_DURATION_RANGE, durationRange: [] })
  }, [searchDispatch])

  const handleOnApply = useCallback(() => {
    trackClientEvent({
      subject: 'filter_applied',
      action: 'applied',
      category: 'cruise_when_filter',
      type: 'interaction',
    })

    const flexibleMonthsLabel = intl.formatMessage({
      id: 'xV3QfiEukhgItwBGd3l3',
      defaultMessage: 'Flexible Months',
      description: 'Label for flexible months filter context in the filters panel',
    })
    const durationRangeLabel = intl.formatMessage({
      id: '7XvoBcN_iszHgaGJn1-C',
      defaultMessage: 'Duration Range',
      description: 'Label for duration range filter context in the search or listing filters panel',
    })
    const checkinDateLabel = intl.formatMessage({
      id: 'eLDbq30q055v_WTkGHl9',
      defaultMessage: 'Check-in Date',
      description: 'Label for check-in date filter context in the search or listing filters panel',
    })
    const checkoutDateLabel = intl.formatMessage({
      id: 'wNib6clO_fegr8pekY-F',
      defaultMessage: 'Check-out Date',
      description: 'Label for check-out date filter context in the search or listing filters panel',
    })

    const filterContexts: Array<{ label: string; type: string; value: string | number }> = []
    if (flexibleMonths) {
      filterContexts.push({ label: flexibleMonthsLabel, type: 'flexible_months', value: flexibleMonths })
    }
    if (durationRange?.length) {
      filterContexts.push({ label: durationRangeLabel, type: 'duration_range', value: durationRange.join(', ') })
    }
    if (checkinDate) {
      filterContexts.push({ label: checkinDateLabel, type: 'checkin_date', value: intlDateFormatter(checkinDate, INTL_DMY_CASUAL_FORMAT, intl.locale) })
    }
    if (checkoutDate) {
      filterContexts.push({ label: checkoutDateLabel, type: 'checkout_date', value: intlDateFormatter(checkoutDate, INTL_DMY_CASUAL_FORMAT, intl.locale) })
    }

    trackEvent(searchEventWithContext({
      searchEvent: { type: 'filters' },
      sortContext: { value: 'recommended' },
      searchContext: mapCruiseLineFilterToSnowplowSearchEvent(globalSearchState),
      filterContexts,
    }))

    onChange({
      flexibleMonths,
      durationMax,
      durationMin,
      durationRange,
      checkinDate,
      checkoutDate,
    })
    closeMobile()
  }, [checkinDate, checkoutDate, durationMax, durationMin, durationRange, flexibleMonths, onChange, closeMobile, globalSearchState, intl])

  const whenLabel = intl.formatMessage({
    id: 'q2qv5ezlrc4s_Z9JYS4N',
    defaultMessage: 'When',
    description: 'Label for the date range filter input in the search and discovery interface',
  })

  const anytimePlaceholder = intl.formatMessage({
    id: 'cdSrWYoX2bEz81_M8hIY',
    defaultMessage: 'Anytime',
    description: 'Placeholder text for when filter input in the search or listing filters panel',
  })

  return <>
    <CruiseFilterInput
      isOpen={isMobileOpen}
      data-testid="cruise-when-select-mobile-input"
      label={whenLabel}
      placeholder={anytimePlaceholder}
      inputValue={inputValue}
      formFieldRef={dropdownTriggerRef}
      onClick={() => {
        trackClientEvent({
          subject: 'filter_opened',
          action: 'opened',
          category: 'cruise_when_filter',
          type: 'interaction',
        })
        toggleMobile()
      }}
      isChipMode={isChipMode}
    />

    <ModalBase
      mode="drawer"
      isOpen={isMobileOpen}
      onClose={closeMobile}
    >
      <ModalHeader
        title={whenLabel}
        onCloseButtonClick={closeMobile}
      />
      <ModalBody>
        <Group direction="vertical" gap={24}>
          <CruiseWhenSelectContent initialFilters={initialFilters} drawerMode />
        </Group>
      </ModalBody>
      <ModalFooter>
        <CruiseFilterFooter
          onApply={handleOnApply}
          onClear={onClear}
          initialFilters={initialFilters}
        />
      </ModalFooter>
    </ModalBase>
  </>
}

export default CruiseWhenSelectMobile
